import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LoadingButton } from "@mui/lab";
import { Box, DialogContent, Stack, Typography } from "@mui/material";
import { DataGrid, GridCellParams, GridColDef } from "@mui/x-data-grid";
import { FileSection, GridCardContent } from "components/atoms";
import {
  AppTable,
  ImportDocumentDialog,
  ImportErrorDialog,
} from "components/molecules";
import { TableHelper } from "helpers";
import { usePaginationRequest, useRequest } from "hooks/useRequest";
import { isArray, isEmpty, isUndefined } from "lodash";
import {
  IBay4S,
  IBay4SDetail,
  IDownloadExcelRequest,
  IGetBay4SDetail,
  IImportBay4sRequest,
  IPaginationRequest,
} from "model";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { DownloadActions, ReportActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { ReportApiService } from "services";
import { EXPORT_FILE_NAME } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";

const _fileId = "import-bay4s-report";

enum EImportType {
  IMPORT_NEW,
  UPDATE,
}

const Bay4SReport = () => {
  const [productSelected, setProductSelected] = useState<{
    productCode: string;
    productLot: string;
  }>({
    productCode: "",
    productLot: "",
  });

  const handleProductSelected = (productCode: string, productLot: string) => {
    setProductSelected({ productCode, productLot });
  };
  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <TableSection onProductSelected={handleProductSelected} />
      <TableDetailSection productSelected={productSelected} />
    </div>
  );
};
const TableSection = (props: {
  onProductSelected(productCode: string, productLot: string): void;
}) => {
  const { onProductSelected } = props;
  const [request, setRequest] = useState({
    pageNumber: 1,
    pageSize: 100,
    searchText: "",
  });
  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    IBay4S[]
  >(request, ReportApiService.getBay4SList);
  const dispatch = useDispatch();
  const errors = useAppSelector((state) => state.ErrorReducer.errors);
  const [file, setFile] = useState<FileList | null>(null);
  const fRef = React.useRef<any>();
  const [open, setOpen] = useState<boolean>(false);
  const [importFailedDialogVisible, setImportFailedDialogVisible] =
    useState<boolean>(false);
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const _importBay4s = () => {
    const request: IImportBay4sRequest = {
      file: file?.item(0) as File,
    };
    dispatch(ReportActions.importBay4s.request(request));
  };
  const handleSelectFile = (files: FileList | null) => {
    if (!isEmpty(files)) {
      setFile(files);
      setOpen(true);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setOpen(false);
  };
  const handleCellClick = (params: GridCellParams<any, any, any>) => {
    const { row } = params;
    onProductSelected(row.productCode, row.productLot);
  };

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber: pageNumber,
    });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const onDownloadInventoryComparisonReport = async () => {
    const params: IDownloadExcelRequest = {
      ...request,
      fileName: EXPORT_FILE_NAME.inventoryComparisonReport,
    };
    dispatch(DownloadActions.downloadInventoryComparisonReport.request(params));
  };
  const onDownloadInventoryComparisonForm = async () => {
    const params: IDownloadExcelRequest = {
      fileName: EXPORT_FILE_NAME.inventoryComparisonForm,
    };
    dispatch(DownloadActions.downloadInventoryComparisonForm.request(params));
  };

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.report.productName),
      size: "full",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
    }),
    TableHelper.createCell({
      field: "uom",
      headerName: I18nTranslator(translations.report.uom),
      size: 100,
      align: "left",
    }),
    TableHelper.createCell({
      field: "unrestricted",
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box>
            <Typography fontWeight={"600"}>
              (SAP){I18nTranslator(translations.report.a)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.unrestricted)}
            </Typography>
          </Box>
        );
      },
    }),
    TableHelper.createCell({
      field: "inQualityInsp",
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box>
            <Typography fontWeight={"600"}>
              (SAP){I18nTranslator(translations.report.b)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.inQualityInsp)}
            </Typography>
          </Box>
        );
      },
    }),
    TableHelper.createCell({
      field: "blocked",
      headerName: "Ghi chú",
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box>
            <Typography fontWeight={"600"}>
              (SAP){I18nTranslator(translations.report.c)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.blocked)}
            </Typography>
          </Box>
        );
      },
    }),
    TableHelper.createCell({
      field: "quantity",
      headerName: "Ghi chú",
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box>
            <Typography fontWeight={"600"}>
              (WMS){I18nTranslator(translations.report.d)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.qrCodeQuantity)}
            </Typography>
          </Box>
        );
      },
    }),
    TableHelper.createCell({
      field: "sumQuantity",
      headerName: "Ghi chú",
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.e)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.eDetail)}
            </Typography>
          </Box>
        );
      },
    }),
    TableHelper.createCell({
      field: "difQuantity",
      headerName: I18nTranslator(translations.report.deviatedDE),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "strManufacturingDate",
      headerName: I18nTranslator(translations.report.manufacturingDate),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "strExpiredDate",
      headerName: I18nTranslator(translations.report.expiryDate),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "palletSpecs",
      headerName: I18nTranslator(translations.report.packSize),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "batchCodeDistributor",
      headerName: I18nTranslator(translations.report.batchCodeDistributor),
      size: "small",
    }),
  ];

  return (
    <Fragment>
      <FileSection ref={fRef} id={_fileId} onFileSelected={handleSelectFile} />
      <AppTable<IBay4S>
        title={I18nTranslator(translations.routes.bay4s)}
        loading={loading}
        data={result}
        columns={COLUMNS}
        onSearch={handleSearch}
        searchPlaceholder={"Tìm kiếm"}
        onCellClick={handleCellClick}
        onChangePage={handleChangePage}
        onFileSelected={handleSelectFile}
        removeActionColumn
        hideAddNew
        hideDownload
        hideImport
        defaultHeaderTable={300}
        headerButtonsLeft={
          <Stack direction={"row"} spacing={2}>
            <label htmlFor={_fileId}>
              <LoadingButton
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => handleSelectFile}
                component={"span"}
              >
                {I18nTranslator(
                  translations.button.inventoryReconciliationImport
                )}
              </LoadingButton>
            </label>
            <LoadingButton
              loading={downloading}
              startIcon={<DownloadIcon />}
              onClick={onDownloadInventoryComparisonForm}
              variant="contained"
            >
              {I18nTranslator(translations.button.exportTemplate)}
            </LoadingButton>
            <LoadingButton
              loading={downloading}
              onClick={onDownloadInventoryComparisonReport}
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              {I18nTranslator(translations.button.exportDocs)}
            </LoadingButton>
          </Stack>
        }
        onPageSizeChange={handlePageSizeChange}
      />
      <ImportDocumentDialog
        title={I18nTranslator(translations.receive.importDocs)}
        open={open}
        handleConfirm={_importBay4s}
        handleClose={handleCancel}
        loading={false}
        fileName={!isEmpty(file) && file ? file[0].name : ""}
        isImportNewDocs={true}
        updateItemName={""}
      />
      <ImportErrorDialog
        title={I18nTranslator(translations.receive.importError)}
        open={importFailedDialogVisible}
        handleReImport={() => {
          setImportFailedDialogVisible(false);
          handleCancel();
        }}
      >
        <DialogContent sx={{ maxHeight: "50vh" }}>
          {!isUndefined(errors) &&
            isArray(errors) &&
            (errors as string[]).map((error) => {
              return <Typography>{error}</Typography>;
            })}
        </DialogContent>
      </ImportErrorDialog>
    </Fragment>
  );
};

const TableDetailSection = (props: {
  productSelected: { productCode: string; productLot: string };
}) => {
  const { productSelected } = props;
  const [request, setRequest] = useState(productSelected);
  const [initialLoad, setInitialLoad] = useState(true);

  const { loading, data } = useRequest<IGetBay4SDetail, IBay4SDetail[]>(
    request,
    ReportApiService.getBay4SDetail,
    [],
    {
      holdRequest: initialLoad,
    }
  );

  useEffect(() => {
    if (!initialLoad) {
      setRequest(productSelected);
    } else {
      setInitialLoad(false);
    }
  }, [productSelected]);

  const COLUMNS2: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "zoneName",
      headerName: I18nTranslator(translations.report.zoneName),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "locationName",
      headerName: I18nTranslator(translations.report.locationName),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.report.palletCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.report.productName),
      size: "full",
    }),
    TableHelper.createCell({
      field: "uom",
      headerName: I18nTranslator(translations.report.uom),
      size: 100,
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
    }),
    TableHelper.createCell({
      field: "palletSpecs",
      headerName: I18nTranslator(translations.report.packSize),
      size: "small",
      align: "right",
    }),

    // TableHelper.createCell({
    //   field: "unrestricted",
    //   size: "small",
    //   align: "left",
    //   renderHeader: () => {
    //     return (
    //       <Box>
    //         <Typography fontWeight={"600"}>(SAP)</Typography>
    //         <Typography fontWeight={"600"}>
    //           {I18nTranslator(translations.report.unrestricted)}
    //         </Typography>
    //       </Box>
    //     );
    //   },
    // }),
    // TableHelper.createCell({
    //   field: "inQualityInsp",
    //   size: "small",
    //   align: "left",
    //   renderHeader: () => {
    //     return (
    //       <Box>
    //         <Typography fontWeight={"600"}>(SAP)</Typography>
    //         <Typography fontWeight={"600"}>
    //           {I18nTranslator(translations.report.inQualityInsp)}
    //         </Typography>
    //       </Box>
    //     );
    //   },
    // }),
    // TableHelper.createCell({
    //   field: "blocked",
    //   headerName: "Ghi chú",
    //   size: "small",
    //   align: "left",
    //   renderHeader: () => {
    //     return (
    //       <Box>
    //         <Typography fontWeight={"600"}>(SAP)</Typography>
    //         <Typography fontWeight={"600"}>
    //           {I18nTranslator(translations.report.blocked)}
    //         </Typography>
    //       </Box>
    //     );
    //   },
    // }),
    TableHelper.createCell({
      field: "quantity",
      headerName: I18nTranslator(translations.report.quantity),
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.quantity)}
            </Typography>
          </Box>
        );
      },
    }),
  ];

  return (
    <GridCardContent sx={{ flexDirection: "column", mt: "10px" }}>
      <div style={{ height: 300, width: "100%" }}>
        <DataGrid
          loading={loading}
          rows={
            data
              ? data.map((item, index) => {
                  return {
                    ...item,
                    id: index + 1,
                    no: index + 1,
                  };
                })
              : []
          }
          columns={COLUMNS2}
          pageSize={5}
          rowsPerPageOptions={[5]}
          disableSelectionOnClick
          hideFooter
          hideFooterPagination
          hideFooterSelectedRowCount
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Không có dữ liệu
              </Stack>
            ),
          }}
        />
      </div>
    </GridCardContent>
  );
};

export default Bay4SReport;
